import React, { useState } from 'react';
import { Text, Container } from 'basis';
import { ExpenseHelpText } from 'components/expenses/ExpenseHelpText';
import {
  Lightbulb,
  ShoppingCart,
  Van,
  Heartbeat,
  BookOpenText,
  Devices,
  Car,
  ShirtFolded,
  Coins,
  HouseLine,
  PersonSimpleThrow,
  Umbrella,
  Barbell,
  Backpack,
  Island,
} from 'components/icons';
import { TextWrapper } from 'components/expenses/ExpenseModal';
import { isFeatureOn } from 'featureToggles';
import { CategoryInfoModal, ExpenseFields, applyUiNzExpenseFieldsProps, applyUiNzCategoryInfo } from 'expense-details';
import { getMonthlyIncome } from 'utils/getMonthlyIncome';
import { yesNoOptions, numbersOnlyPattern } from '../_constants';

const dollarAmountValidationMessages = {
  required: 'You must enter a dollar amount.',
  invalid: 'Please enter a whole dollar amount, without cents.',
  frequencyRequired: 'Please select a frequency',
  maxValue: 'Must not be more than $99,999,999.',
  maxLength: 'Must not be more than $99,999,999.',
};

const validationMessages = {
  dollarAmount: dollarAmountValidationMessages,
};

const individualExpenseMaxLength = 8;
const summarisedExpenseMaxLength = 9;

const helpText = {
  basic: {
    text: 'e.g. utilities, food, transport, clothing. ',
    hyperLinkText: 'See full list.',
    suffixText: ' Do not include your home loan, rent or board payments.',
  },
  additional: {
    text:
      'e.g. non-essential insurances, private school fees, second dwellings, include any expense that you pay using Buy Now Pay Later (BNPL) services. ',
    hyperLinkText: 'See full list.',
  },
  otherHouseCost: {
    text: 'e.g. electricity, gas, water, telephone, internet, streaming subscriptions and ',
    hyperLinkText: 'more about other housing costs',
    suffixText: '.',
  },
};

const modal = {
  basic: {
    title: 'What are basic expenses?',
    content: (
      <>
        <Text>Basic expenses are essential costs that are necessary for living in New Zealand.</Text>
        <Container margin="4 0">
          <TextWrapper>They include:</TextWrapper>
        </Container>
      </>
    ),
    items: [
      {
        icon: <Lightbulb />,
        title: 'Utilities',
        list: ['Electricity', 'Gas', 'Water', 'Other essential services'],
      },
      {
        icon: <ShoppingCart />,
        title: 'Food and groceries',
        list: ['Regular spending on food', 'Household supplies'],
      },
      {
        icon: <Van />,
        title: 'Transport',
        list: [
          'Costs related to owning and operating a vehicle',
          'Public transport',
          'Other necessary travel expenses',
        ],
      },
      {
        icon: <Heartbeat />,
        title: 'Health and medical costs',
        list: ['Doctor visits', 'Medications', 'Other medical expenses'],
      },
      {
        icon: <BookOpenText />,
        title: 'Public education and childcare',
        list: ['School fees', 'Childcare costs', 'Related expenses'],
      },
      {
        icon: <Devices />,
        title: 'Communication and connectivity',
        list: ['Telephone', 'Internet', 'Other communication services'],
      },
      {
        icon: <Car />,
        title: 'Essential insurances',
        list: ['Home', 'Contents', 'Vehicle insurance'],
      },
      {
        icon: <ShirtFolded />,
        title: 'Clothing and personal care',
        list: ['Basic clothing', 'Personal hygiene products'],
      },
      {
        icon: <HouseLine />,
        title: 'Other housing costs',
        list: [
          'Land tax (if applicable)',
          'Council rates',
          'Body corporate fees',
          'Maintenance costs for your primary residence',
        ],
      },
    ],
  },
  additional: {
    title: 'What are additional expenses?',
    content: (
      <>
        <Text>These expenses are not essential, but are part of lifestyle choices.</Text>
        <Container margin="4 0 0 0">
          <TextWrapper>They include:</TextWrapper>
        </Container>
      </>
    ),
    items: [
      {
        icon: <PersonSimpleThrow />,
        title: 'Recreational and entertainment costs',
        list: ['Spending on hobbies', 'Takeaway / dining out', 'Other leisure activities'],
      },
      {
        icon: <Umbrella />,
        title: 'Non-essential insurances',
        list: [
          'Health insurance premiums',
          'Policies such as life',
          'Total and permanent disability (TPD)',
          'Insurance outside of your superannuation fund',
          'Insurance for classic or rare cars',
          'Additional superannuation contributions',
        ],
      },
      {
        icon: <Barbell />,
        title: 'Subscriptions and memberships',
        list: ['Costs for streaming services', 'Other subscriptions', 'Gym memberships'],
      },
      {
        icon: <Backpack />,
        title: 'Private school fees',
        list: ['Education costs for private schooling'],
      },
      {
        icon: <HouseLine />,
        title: 'Second dwellings',
        list: ['Expenses related to owning additional property(ies)'],
      },
      {
        icon: <Island />,
        title: 'Holidays',
        list: ['Domestic travel', 'International/overseas travel'],
      },
    ],
    footerContent: <Text>Include any expense that you pay using Buy Now Pay Later (BNPL) services.</Text>,
  },
  otherHouseCost: {
    title: 'Other housing costs',
    items: [
      {
        icon: <Coins />,
        title: 'Other housing costs',
        list: ['Land tax (if applicable)', 'Council rates', 'Domestic travel', 'International/overseas travel'],
      },
      {
        icon: <HouseLine />,
        title: 'Second dwellings',
        list: ['Expenses related to owning additional property(ies)'],
      },
    ],
  },
};

/**
 * Wraps `ExpenseFields` so its "See full list."/"more about other housing costs" help-text
 * links open `expense-details`'s own `CategoryInfoModal`, populated from its `applyUiNzCategoryInfo` —
 * `ExpenseFields` itself never renders a modal, it only calls `onCategoryInfoRequest` with the
 * category key.
 */
export function ExpenseFieldsWithModal(props) {
  const [activeModalKey, setActiveModalKey] = useState(null);
  const employmentDetails = props.storeState?.acquisition?.employmentDetails ?? {};
  const monthlyIncome = getMonthlyIncome(employmentDetails);

  return (
    <>
      <ExpenseFields {...props} monthlyIncome={monthlyIncome} onCategoryInfoRequest={setActiveModalKey} />
      <CategoryInfoModal
        categoryInfo={activeModalKey ? applyUiNzCategoryInfo[activeModalKey] : null}
        onClose={() => setActiveModalKey(null)}
      />
    </>
  );
}

export const expensesDetails = [
  // `expensesLibrary` (see featureToggles/defaultValues) — every field below is unchanged;
  // each one just gets an extra `!isFeatureOn('expensesLibrary')` condition (ANDed into any
  // existing condition) rather than being wrapped in a new nesting level. Some shared utilities
  // (e.g. `toSaveAndSubmitApplicationInput.js`'s `findFieldConfig`) look up a field's config by
  // name via a fragile, non-standard traversal that breaks if a field's nesting depth changes —
  // so the flag check is added per-field/per-group here, not via a wrapping group.
  {
    name: 'isExpensesShared',
    label: 'Do you share your living expenses?',
    component: 'RadioTile',
    options: yesNoOptions,
    showCircles: true,
    testId: 'is-expenses-shared',
    helpText: "Select 'Yes' if you share costs in anyway with your partner or spouse.",
    condition: () => !isFeatureOn('expensesLibrary'),
  },
  {
    component: 'Frequency',
    name: 'primaryPropertyMortgageRentBoard',
    label: 'Enter only the amount you pay for rent, mortgage or board',
    testId: 'mortgage-rent-board',
    helpText:
      'Enter only the amount you pay (not the total household amount). Exclude repayments on investment properties.',
    mode: 'select',
    amountPrefix: '$',
    selectPlaceholder: 'Frequency',
    annually: false,
    validate: {
      props: { maxLength: 8, validPattern: numbersOnlyPattern },
      messages: validationMessages.dollarAmount,
      component: 'Frequency',
    },
    condition: () => !isFeatureOn('expensesLibrary'),
  },
  {
    name: 'isSummaryExpenses',
    label: 'Add your living expenses',
    component: 'RadioTile',
    options: [
      {
        label: 'I know my total living expenses',
        value: 'yes',
      },
      {
        label: 'Guide me through it (by category)',
        value: 'no',
      },
    ],
    showCircles: true,
    testId: 'is-summary-expenses',
    validate: {
      messages: {
        required: 'Please make a selection.',
      },
    },
    condition: () => !isFeatureOn('expensesLibrary'),
  },
  {
    condition: values => values.isSummaryExpenses === 'no' && !isFeatureOn('expensesLibrary'),
    component: 'CustomComponent',
    children: () => <Text>Include any expense that you pay using Buy Now Pay Later (BNPL) services.</Text>,
  },
  {
    condition: values => values.isSummaryExpenses === 'no' && !isFeatureOn('expensesLibrary'),
    fields: [
      /**
       * Your residential expenses
       ** */
      {
        component: 'Frequency',
        name: 'foodGroceriesLifestyleEntertainment',
        label: values =>
          values.isExpensesShared === 'yes'
            ? 'Your share of food, holidays, recreation and entertainment'
            : 'Food, holidays, recreation and entertainment',
        testId: 'food-groceries-lifestyle-entertainment',
        helpText:
          'e.g. regular spend on food, household supplies, travel, hobbies, dining out and other leisure activities.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'Frequency',
        name: 'generalInsurance',
        label: values => (values.isExpensesShared === 'yes' ? 'Your share of insurance' : 'Insurance'),
        testId: 'general-insurance',
        helpText: 'e.g. home, contents, health, vehicle, additional superannuation contributions.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'Frequency',
        name: 'propertyUtilitiesOther',
        label: values =>
          values.isExpensesShared === 'yes'
            ? 'Your share of utilities and other housing costs'
            : 'Utilities and other housing costs',
        testId: 'utilities-and-other-property-expenses',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'CustomComponent',
        children: () => (
          <ExpenseHelpText id="otherHouseCost" {...helpText.otherHouseCost} modal={modal.otherHouseCost} />
        ),
      },
      {
        component: 'Frequency',
        name: 'educationChildcare',
        label: values =>
          values.isExpensesShared === 'yes' ? 'Your share of education and childcare' : 'Education and childcare',
        testId: 'education-childcare',
        helpText: 'e.g. public and private school fees, childcare costs and related expenses.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'Frequency',
        name: 'transport',
        label: values => (values.isExpensesShared === 'yes' ? 'Your share of transport' : 'Transport'),
        testId: 'transport-auto',
        helpText: 'e.g. public transport, registration, servicing, petrol, parking fees and road tolls.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'Frequency',
        name: 'personalFitnessMedical',
        label: values =>
          values.isExpensesShared === 'yes'
            ? 'Your share of clothing, personal care, health and medical'
            : 'Clothing, personal care, health and medical',
        testId: 'personal-fitness-medical',
        helpText: 'e.g. clothing, personal hygiene products, gym memberships, doctor visits or medications.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
      {
        component: 'Frequency',
        name: 'other',
        label: values =>
          values.isExpensesShared === 'yes' ? 'Your share of additional living expenses' : 'Additional living expenses',
        testId: 'any-additional-expenses',
        helpText:
          'e.g. health insurance, life insurance, property rates, child maintenance, donations, gifts to family and/or tithings to your church. Also include any expenses that you need to pay that are less frequent or not disclosed above.',
        selectPlaceholder: 'Frequency',
        mode: 'select',
        amountPrefix: '$',
        annually: false,
        validate: {
          props: { maxLength: individualExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: validationMessages.dollarAmount,
          component: 'Frequency',
        },
      },
    ],
  },
  {
    condition: values => values.isSummaryExpenses === 'yes' && !isFeatureOn('expensesLibrary'),
    fields: [
      {
        component: 'Frequency',
        name: 'basicExpenses',
        label: values =>
          values.isExpensesShared === 'yes' ? 'Your share of basic living expenses' : 'Basic living expenses',
        testId: 'basic-expenses',
        mode: 'select',
        amountPrefix: '$',
        selectPlaceholder: 'Frequency',
        quarterly: false,
        amountMaxLength: summarisedExpenseMaxLength,
        validate: {
          props: { summarisedExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: {
            ...dollarAmountValidationMessages,
            required: 'Please enter a value greater than $1 that represents your basic expenses.',
          },
          component: 'Frequency',
        },
      },
      {
        component: 'CustomComponent',
        children: () => <ExpenseHelpText id="basic" {...helpText.basic} modal={modal.basic} />,
      },
      {
        component: 'Frequency',
        name: 'additionalExpenses',
        label: values =>
          values.isExpensesShared === 'yes' ? 'Your share of additional living expenses' : 'Additional living expenses',
        testId: 'additional-expenses',
        mode: 'select',
        amountPrefix: '$',
        selectPlaceholder: 'Frequency',
        quarterly: false,
        amountMaxLength: summarisedExpenseMaxLength,
        validate: {
          props: { summarisedExpenseMaxLength, validPattern: numbersOnlyPattern },
          messages: {
            ...dollarAmountValidationMessages,
            required: 'Please enter a value greater than $1 that represents your additional expenses.',
          },
          component: 'Frequency',
        },
      },
      {
        component: 'CustomComponent',
        children: () => <ExpenseHelpText id="additional" {...helpText.additional} modal={modal.additional} />,
      },
    ],
  },
  {
    condition: () => isFeatureOn('expensesLibrary'),
    component: 'CustomComponent',
    children: ExpenseFieldsWithModal,
    name: 'expenses',
    testId: 'expenses-lib',
    ...applyUiNzExpenseFieldsProps,
  },
];
